/**
 * Calculs de calendrier et de fuseau horaire du parcours de rendez-vous (POC moteur d'implémentation,
 * §9A et §10.2). Sans bibliothèque : `Intl` porte les fuseaux IANA. Toutes les dates échangées sont
 * des instants UTC en ISO 8601 ; seul l'affichage passe dans le fuseau choisi par le visiteur.
 */

export interface CalendarDay {
  /** Date civile `AAAA-MM-JJ` dans le fuseau d'affichage. */
  date: string;
  day: number;
  in_month: boolean;
}

function parts(instant: Date, timeZone: string): Record<string, number> {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const out: Record<string, number> = {};
  for (const part of formatter.formatToParts(instant)) if (part.type !== 'literal') out[part.type] = Number(part.value);
  return out;
}

/** Décalage du fuseau à cet instant, en millisecondes (positif à l'est de Greenwich). */
export function zoneOffset(instant: Date, timeZone: string): number {
  const p = parts(instant, timeZone);
  const asUtc = Date.UTC(p['year']!, p['month']! - 1, p['day']!, p['hour']!, p['minute']!, p['second']!);
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** Heure civile d'un fuseau → instant UTC. Deux passes pour traverser un changement d'heure. */
export function zonedToUtc(date: string, hour: number, minute: number, timeZone: string): Date {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  let instant = guess - zoneOffset(new Date(guess), timeZone);
  instant = guess - zoneOffset(new Date(instant), timeZone);
  return new Date(instant);
}

/** Date civile `AAAA-MM-JJ` d'un instant dans un fuseau. */
export function civilDate(instant: Date, timeZone: string): string {
  const p = parts(instant, timeZone);
  return `${p['year']}-${String(p['month']).padStart(2, '0')}-${String(p['day']).padStart(2, '0')}`;
}

export function addDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

/** Jour de la semaine d'une date civile, lundi = 1 … dimanche = 7. */
export function isoWeekday(date: string): number {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

/** Grille d'un mois `AAAA-MM`, semaines commençant le lundi, complétée par les mois voisins. */
export function monthGrid(month: string): CalendarDay[][] {
  const first = `${month}-01`;
  let cursor = addDays(first, 1 - isoWeekday(first));
  const weeks: CalendarDay[][] = [];
  do {
    const week: CalendarDay[] = [];
    for (let i = 0; i < 7; i += 1) {
      week.push({ date: cursor, day: Number(cursor.slice(8, 10)), in_month: cursor.startsWith(month) });
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
  } while (cursor.startsWith(month));
  return weeks;
}

export function shiftMonth(month: string, delta: number): string {
  const [year, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(year, m - 1 + delta, 1)).toISOString().slice(0, 7);
}

/** Libellés courts des jours, du lundi au dimanche, dans la langue d'affichage. */
export function weekdayLabels(locale = 'fr-FR'): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  // 2026-01-05 est un lundi.
  return Array.from({ length: 7 }, (_, i) => formatter.format(new Date(Date.UTC(2026, 0, 5 + i))));
}

export function formatMonth(month: string, locale = 'fr-FR'): string {
  const [year, m] = month.split('-').map(Number) as [number, number];
  const label = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(year, m - 1, 1)));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatDay(date: string, locale = 'fr-FR'): string {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const label = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, day)));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatTime(iso: string, timeZone: string, locale = 'fr-FR'): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone }).format(new Date(iso));
}

export function formatDateTime(iso: string, timeZone: string, locale = 'fr-FR'): string {
  const label = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone }).format(new Date(iso));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Libellé court d'un fuseau, avec l'heure courante : « Europe/Paris (14:05) ». */
export function zoneLabel(timeZone: string, now: Date): string {
  return `${timeZone.replace(/_/g, ' ')} (${formatTime(now.toISOString(), timeZone)})`;
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris';
  } catch {
    return 'Europe/Paris';
  }
}

function icsStamp(iso: string): string {
  return iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function icsText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

/** Invitation iCalendar (RFC 5545) : instants en UTC, aucun lien de visioconférence inventé. */
export function icsInvite(input: { uid: string; start: string; end: string; summary: string; description: string; stamp: string }): string {
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//AYUMIA//Implementation POC//FR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${input.uid}`,
    `DTSTAMP:${icsStamp(input.stamp)}`,
    `DTSTART:${icsStamp(input.start)}`,
    `DTEND:${icsStamp(input.end)}`,
    `SUMMARY:${icsText(input.summary)}`,
    `DESCRIPTION:${icsText(input.description)}`,
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n');
}
